import { AlertColor, AnimationAlert, VariantAlert } from '@/shared/types/alert';

interface AlertClassNameProps {
  color: AlertColor;
  variant: VariantAlert;
  isLeaving?: boolean;
  animation?: AnimationAlert;
}

/**
 * Genera las clases CSS para el Alert usando tokens semánticos de Figma
 *
 * Mapeo de variantes:
 * - contained → Solid (Figma): fondo sólido de color
 * - outlined → Soft (Figma): fondo claro con borde de color
 * - accent: borde lateral grueso
 * - minimal: solo texto coloreado
 *
 * Tokens de Figma utilizados:
 * - Border radius: control-sm (8px)
 * - Border width: 1px
 * - Colores: bg-surface-*, bg-fill-*, border-all-colors-*, text-*
 */
export const getAlertClassName = ({
  color = 'danger',
  variant = 'contained',
  isLeaving = false,
  animation = 'none',
}: AlertClassNameProps) => {
  // Clases base con tokens semánticos
  const baseClasses = [
    'ui-rounded-control-sm', // border-radius: 8px (Figma)
    'ui-shadow-01',
    'ui-transform',
    'ui-transition-all',
    'ui-duration-300',
    'ui-ease-in-out',
    'ui-w-full',
    'ui-max-w-full',
    'ui-min-w-0',
    'ui-flex-shrink-0',
    'ui-box-border',
  ].join(' ');

  const listAnimationClasses = {
    none: {
      in: 'ui-opacity-100',
      out: 'ui-opacity-0',
    },
    fade: {
      in: 'ui-opacity-100 ui-animate-fade-in',
      out: 'ui-opacity-0 ui-animate-fade-out',
    },
    slide: {
      in: 'ui-opacity-100 ui-translate-x-0',
      out: 'ui-opacity-0 ui-translate-x-4',
    },
    zoom: {
      in: 'ui-opacity-100 ui-scale-100',
      out: 'ui-opacity-0 ui-scale-95',
    },
    flip: {
      in: 'ui-opacity-100 ui-rotate-0',
      out: 'ui-opacity-0 ui-rotate-180',
    },
    bounce: {
      in: 'ui-opacity-100 ui-scale-100 ui-animate-bounce-in',
      out: 'ui-opacity-0 ui-scale-95 ui-animate-bounce-out',
    },
    'slide-up': {
      in: 'ui-opacity-100 ui-translate-y-0',
      out: 'ui-opacity-0 ui-translate-y-4',
    },
  };

  const animationClasses = isLeaving
    ? listAnimationClasses[animation].out
    : listAnimationClasses[animation].in;

  /**
   * Colores usando tokens semánticos de Figma
   * El fondo siempre debe ser blanco según el diseño de Figma
   *
   * contained (Solid): bg-fill (blanco), border-all-colors-*, text-*
   * outlined (Soft): bg-fill (blanco), border-all-colors-*, text-*
   * accent: bg-fill (blanco), text-*, border-l-4
   * minimal: bg-fill (blanco), text-*
   */
  const typeClasses: Record<AlertColor, Record<VariantAlert, string>> = {
    success: {
      contained:
        'ui-bg-alert-success-bg ui-border-1 ui-border-solid ui-border-alert-success-border ui-text-alert-success-text',
      outlined: 'ui-bg-fill ui-border-1 ui-border-solid ui-border-default ui-text-success',
      accent:
        'ui-bg-alert-success-bg ui-text-alert-success-text ui-border-l-4 ui-border-solid ui-border-alert-success-border',
      minimal: 'ui-bg-fill ui-text-alert-success-text',
    },
    danger: {
      contained:
        'ui-bg-alert-danger-bg ui-border-1 ui-border-solid ui-border-alert-danger-border ui-text-alert-danger-text',
      outlined: 'ui-bg-fill ui-border-1 ui-border-solid ui-border-default ui-text-danger',
      accent:
        'ui-bg-alert-danger-bg ui-text-alert-danger-text ui-border-l-4 ui-border-solid ui-border-alert-danger-border',
      minimal: 'ui-bg-fill ui-text-alert-danger-text',
    },
    info: {
      contained:
        'ui-bg-alert-info-bg ui-border-1 ui-border-solid ui-border-alert-info-border ui-text-alert-info-text',
      outlined: 'ui-bg-fill ui-border-1 ui-border-solid ui-border-default ui-text-info',
      accent:
        'ui-bg-alert-info-bg ui-text-alert-info-text ui-border-l-4 ui-border-solid ui-border-alert-info-border',
      minimal: 'ui-bg-fill ui-text-alert-info-text',
    },
    warning: {
      contained:
        'ui-bg-alert-warning-bg ui-border-1 ui-border-solid ui-border-alert-warning-border ui-text-alert-warning-text',
      outlined: 'ui-bg-fill ui-border-1 ui-border-solid ui-border-default ui-text-warning',
      accent:
        'ui-bg-alert-warning-bg ui-text-alert-warning-text ui-border-l-4 ui-border-solid ui-border-alert-warning-border',
      minimal: 'ui-bg-fill ui-text-alert-warning-text',
    },
    neutral: {
      contained: 'ui-bg-fill ui-border-1 ui-border-solid ui-border-default ui-text-secondary',
      outlined: 'ui-bg-fill ui-border-1 ui-border-solid ui-border-default ui-text-secondary',
      accent: 'ui-bg-fill ui-text-secondary ui-border-l-4 ui-border-solid ui-border-default',
      minimal: 'ui-bg-fill ui-text-secondary',
    },
    brand: {
      contained: 'ui-bg-fill ui-border-1 ui-border-solid ui-border-default ui-text-brand',
      outlined: 'ui-bg-fill ui-border-1 ui-border-solid ui-border-default ui-text-brand',
      accent: 'ui-bg-fill ui-text-brand ui-border-l-4 ui-border-solid ui-border-default',
      minimal: 'ui-bg-fill ui-text-brand',
    },
  };

  // Usar 'danger' como fallback si el color no existe
  const colorClasses = typeClasses[color] || typeClasses.danger;

  // Validación en desarrollo
  if (process.env.NODE_ENV !== 'production') {
    if (!typeClasses[color]) {
      // eslint-disable-next-line no-console
      console.warn(`Alert: Invalid color "${color}", using "danger" as fallback`);
    }
    if (!colorClasses[variant]) {
      // eslint-disable-next-line no-console
      console.warn(
        `Alert: Invalid variant "${variant}" for color "${color}", using "contained" as fallback`
      );
    }
  }

  return `${baseClasses} ${animationClasses} ${colorClasses[variant] || colorClasses.contained}`;
};
